import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { errorSnackBar, successSnackBar } from "../actions/common.action";

const OAUTH_RESULT_TYPES = {
  XERO_CONNECTION_RESULT: 'Xero',
  QUICKBOOKS_CONNECTION_RESULT: 'QuickBooks',
  MYOB_CONNECTION_RESULT: 'MYOB'
};

const STORAGE_KEYS = {
  xero_connection_result: 'Xero',
  quickbooks_connection_result: 'QuickBooks',
  myob_connection_result: 'MYOB'
};

/**
 * Custom hook to register and clean up window event listeners.
 * @param {string} eventName - Target DOM window event (e.g. 'message', 'storage')
 * @param {Function} handler - Event callback handler
 */
function useEventListener(eventName, handler) {
  useEffect(() => {
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [eventName, handler]);
}

/**
 * Custom hook providing OAuth connection initiation and message/storage callback handling
 * across accounting platforms (Xero, QuickBooks, MYOB).
 *
 * @param {string|number} accountId - Authenticated user account ID
 * @param {Function} fetchConnections - Callback to reload active connection list upon connect
 * @returns {{ handleConnect: Function }} Hook interface
 */
export function useAccountingOAuth(accountId, fetchConnections) {
  const dispatch = useDispatch();

  const processConnectionResult = useCallback(async (result = {}, platformName) => {
    const { status, error } = result;

    if (status === 'success') {
      dispatch(successSnackBar(`${platformName} Connected Successfully`));
      await fetchConnections();
    } else if (error && error !== 'access_denied') {
      dispatch(errorSnackBar(error || `Failed to connect to ${platformName}`));
    }
  }, [fetchConnections, dispatch]);

  const handleOAuthMessage = useCallback(async (e) => {
    const platformName = OAUTH_RESULT_TYPES[e.data?.type];
    if (platformName) {
      await processConnectionResult(e.data, platformName);
    }
  }, [processConnectionResult]);

  const handleStorageChange = useCallback(async (e) => {
    const platformName = STORAGE_KEYS[e?.key];
    if (!platformName || !e?.newValue) return;

    try {
      const result = JSON.parse(e.newValue);
      await processConnectionResult(result, platformName);
      localStorage.removeItem(e.key);
    } catch (err) { // Fix: catch keyword added here
      console.error(`Error handling ${platformName} callback storage event:`, err);
    }
  }, [processConnectionResult]);

  useEventListener('message', handleOAuthMessage);
  useEventListener('storage', handleStorageChange);


  const handleConnect = useCallback((platform, forceLogin = false, connectionId = null) => {
    if (!accountId) return;
    const apiBase = process.env.REACT_APP_ACCOUNT_API || 'http://localhost:7000';
    let url = `${apiBase}/platform-connections/${platform.toLowerCase()}/connect`;

    const params = new URLSearchParams();
    if (platform.toLowerCase() === 'xero' && forceLogin) {
      params.append('prompt', 'login consent');
    }
    if (connectionId) {
      params.append('connectionId', connectionId);
    }

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const width = 750;
    const height = 800;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    // Dynamic popup name for all platforms (Xero, QuickBooks, MYOB)
    const popupName = `Connect ${platform.charAt(0).toUpperCase() + platform.slice(1)}`;

    window.open(url, popupName, `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
  }, [accountId]);

  return { handleConnect };
}